import React, { useState, useEffect } from "react";
import "../../../App.css";
import { apiPostAuth } from "../../../utils/api"; // Use your axios wrapper
import { useNavigate } from "react-router-dom";

export default function AddAppStoreAppPage() {
  const [issuerId, setIssuerId] = useState("");
  const [appId, setAppId] = useState("");
  const [keyId, setKeyId] = useState("");
  const [p8File, setP8File] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();

  const validateIssuerId = (issuerId) => {
    const regex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/;
    return regex.test(issuerId);
  };

  const validateAppId = (str) => {
    return /^\d{9,10}$/.test(str);
  };

  const validateKeyId = (str) => {
    return /^[A-Z0-9]{10}$/.test(str);
  };

  const validateP8File = async (file) => {
    if (!file) {
      return { valid: false, message: "No file selected." };
    }

    // 1. Check extension
    if (!file.name.toLowerCase().endsWith(".p8")) {
      return { valid: false, message: "File must be a .p8 file." };
    }

    // 2. Check size (Apple keys are very small, usually < 5KB)
    if (file.size > 10 * 1024) {
      return { valid: false, message: "File is too large to be a valid .p8 key." };
    }

    // 3. Read content
    const text = await file.text();

    // 4. Check PEM structure
    if (
      !text.includes("-----BEGIN PRIVATE KEY-----") ||
      !text.includes("-----END PRIVATE KEY-----")
    ) {
      return { valid: false, message: "Invalid .p8 key format." };
    }

    // 5. Optional: stricter PEM validation
    const pemRegex = /-----BEGIN PRIVATE KEY-----[\s\S]+-----END PRIVATE KEY-----/;
    if (!pemRegex.test(text)) {
      return { valid: false, message: "Malformed private key." };
    }

    return { valid: true };
  };

  const handleConnect = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    if (!issuerId.trim()) {
      setErrorMessage("Please enter the Issuer ID.");
      return;
    }

    if (!keyId.trim()) {
      setErrorMessage("Please enter the Key ID.");
      return;
    }

    if (!appId.trim()) {
      setErrorMessage("Please enter the App ID.");
      return;
    }

    if (!validateIssuerId(issuerId)) {
      setErrorMessage(
        "Invalid Issuer ID format. Example: 00000000-0000-0000-0000-000000000000"
      );
      return;
    }

    if (!validateKeyId(keyId)) {
      setErrorMessage(
        "Invalid App ID format. Example: ABCDE12345"
      );
      return;
    }

    if (!validateAppId(appId)) {
      setErrorMessage(
        "Invalid App ID format. Example: 1234567890"
      );
      return;
    }

    const p8FileValidation = await validateP8File(p8File);

    if (!p8FileValidation.valid) {
      setErrorMessage(
        "Invalid .p8 API Key"
      );
      return;
    }

    try {
      const response = await apiPostAuth("apple_apps/add_app.php", {
        issuer_id: issuerId,
        key_id: keyId,
        p8_file: await fileToText(p8File),
        app_id: appId
      });
      if (response.result === 1) {
        
        navigate("/dashboard/add-app-store-app/app-detail", {
          state: { appId },
        });
      } else {
        setErrorMessage(response.message || "Failed to connect app.");
      }
    } catch (error) {
      setErrorMessage("Oops, something went wrong. Please try again.");
    }
  };

  const fileToText = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error);

      reader.readAsText(file);
    });
  };

  if (loading) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <div className="loading-spinner"></div>
        <p style={{ marginTop: "10px", color: "#4b5563" }}>Loading...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: "20px" }}>
      <button
        onClick={() => navigate(`/dashboard/google-play-review`)}
        style={backButtonStyle}
        >
        ← Back to App List
      </button>

      <h1 style={{ fontSize: "1.8rem", marginBottom: "10px", color: "#1f2937" }}>
        Add App Store App or Game
      </h1>
      <p style={{ fontSize: "1rem", marginBottom: "30px", color: "#4b5563" }}>
        Connect a new App Store app or game to automate your review responses.
      </p>

      {/* White container filling parent width */}
      <div
        style={{
          backgroundColor: "#fff",
          padding: "30px 20px",
          borderRadius: "8px",
          boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
          width: "90%",
          marginBottom: "20px",
        }}
      >
        <h2 style={{ fontSize: "1.2rem", marginBottom: "10px", color: "#1f2937" }}>
          Steps to Connect App/Game:
        </h2>
        <ol style={{ color: "#4b5563", lineHeight: "1.6", paddingLeft: "20px" }}>
          <li>
            <strong>Generate API Key & Key ID: </strong>
            Go to your App Store Connect dashboard. Navigate to{" "}<em>Users and Access</em>. Go to the <em>Team Keys</em> under the {" "}
            <em>Integrations</em>. Click on the add icon to create API Key. Fill the name for the API Key and select the following Roles:
            <ul style={{ marginTop: "5px", paddingLeft: "20px" }}>
              <li>Customer Support</li>
            </ul>
            <strong>Ensure the app you want to connect is allowed. </strong>
            Download the generated <strong>.p8 API Key</strong> (This .p8 key can only be downloaded once) and copy the <strong>Key ID</strong>.
          </li>
          <li style={{ marginTop: "10px" }}>
            <strong>Obtain Issuer ID: </strong> Under the <em>Users and Access</em> page, go to <em>Integrations</em> tab, copy the Issuer ID. Enter the Issuer ID below.
          </li>
          <li style={{ marginTop: "10px" }}>
            <strong>Obtain App ID: </strong> App ID or Apple ID is your app's identifier located under the <em>App Information</em>
          </li>
        </ol>

        {/* Form inside the same container */}
        <form onSubmit={handleConnect} style={{ marginTop: "20px" }}>
          <label
            htmlFor="issuerId"
            style={{
              display: "block",
              marginBottom: "8px",
              color: "#1f2937",
              fontWeight: "500",
            }}
          >
            Issuer ID
          </label>
          <input
            id="issuerId"
            type="text"
            value={issuerId}
            onChange={(e) => setIssuerId(e.target.value)}
            placeholder="00000000-0000-0000-0000-000000000000"
            style={{
              width: "90%",
              padding: "10px",
              border: "1px solid #ccc",
              borderRadius: "6px",
              marginBottom: "10px",
              fontSize: "1rem",
            }}
          />
          <label
            htmlFor="keyId"
            style={{
              display: "block",
              marginBottom: "8px",
              color: "#1f2937",
              fontWeight: "500",
            }}
          >
            Key ID
          </label>
          <input
            id="keyId"
            type="text"
            value={keyId}
            onChange={(e) => setKeyId(e.target.value)}
            placeholder="ABCDE12345"
            style={{
              width: "90%",
              padding: "10px",
              border: "1px solid #ccc",
              borderRadius: "6px",
              marginBottom: "10px",
              fontSize: "1rem",
            }}
          />
          <label
            htmlFor="p8File"
            style={{
              display: "block",
              marginBottom: "8px",
              color: "#1f2937",
              fontWeight: "500",
            }}
          >
            .p8 API Key (This key will be encrypted in our server)
          </label>
          <input
            id="p8File"
            type="file"
            accept=".p8"
            onChange={(e) => setP8File(e.target.files[0])}
            style={{
              width: "90%",
              padding: "10px",
              border: "1px solid #ccc",
              borderRadius: "6px",
              marginBottom: "10px",
              fontSize: "1rem",
            }}
          />
          <label
            htmlFor="appId"
            style={{
              display: "block",
              marginBottom: "8px",
              color: "#1f2937",
              fontWeight: "500",
            }}
          >
            App ID
          </label>
          <input
            id="appId"
            type="text"
            value={appId}
            onChange={(e) => setAppId(e.target.value)}
            placeholder="1234567890"
            style={{
              width: "90%",
              padding: "10px",
              border: "1px solid #ccc",
              borderRadius: "6px",
              marginBottom: "10px",
              fontSize: "1rem",
            }}
          />
          {errorMessage && (
            <p style={{ color: "red", fontSize: "0.9rem", marginBottom: "10px" }}>
              {errorMessage}
            </p>
          )}
          <button
            type="submit"
            className="primary-button"
            style={{ width: "150px" }}
          >
            Connect
          </button>
          <p style={{ fontSize: "0.85rem", marginTop: "8px", color: "#6b7280" }}>
            *This step is just for setup and does not start automation.
          </p>
        </form>
      </div>
    </div>
  );
}

const backButtonStyle = {
  display: "inline-block",
  padding: "8px 16px",
  backgroundColor: "#f2f6fc",
  color: "#2563eb",
  fontWeight: "500",
  border: "1px solid #d1d5db",
  borderRadius: "6px",
  cursor: "pointer",
  marginBottom: "20px",
  transition: "all 0.2s ease",
};

backButtonStyle[":hover"] = {
  backgroundColor: "#e5e7eb",
};
